import { toLocalMeters, fromLocalMeters, longestEdge, } from "@energyos/geometry-core";
import { polygonAreaPlanar } from "@energyos/geometry-core";
import { moduleIsPlaceable } from "./clip.js";
import { obstacleClearanceM, roofEdgeSetbackM, exclusionClearanceM } from "./setbacks.js";
const EPS = 1e-6;
function dot(a, b) {
    return a.x * b.x + a.y * b.y;
}
function unit(v) {
    const m = Math.hypot(v.x, v.y) || 1;
    return { x: v.x / m, y: v.y / m };
}
function eaveOf(plane) {
    if (plane.eave)
        return plane.eave;
    const e = longestEdge(plane.vertices);
    if (!e)
        throw new RangeError("Dachpolygon hat keine Kante (≥ 2 Punkte nötig)");
    return { a: e.a, b: e.b };
}
function buildBasis(plane) {
    const eave = eaveOf(plane);
    const origin = eave.a;
    const u = unit(toLocalMeters(eave.b, origin));
    let v = { x: -u.y, y: u.x }; // +90°
    // v soll ins Polygon-Innere zeigen → Test mit (lokalem) Schwerpunkt
    let cx = 0;
    let cy = 0;
    for (const p of plane.vertices) {
        const l = toLocalMeters(p, origin);
        cx += l.x;
        cy += l.y;
    }
    const centroid = { x: cx / plane.vertices.length, y: cy / plane.vertices.length };
    if (dot(centroid, v) < 0)
        v = { x: u.y, y: -u.x };
    return { origin, u, v };
}
/** Geo-Polygon → ebene (u,v)-Meter. */
function to2d(points, b) {
    return points.map((p) => {
        const l = toLocalMeters(p, b.origin);
        return { x: dot(l, b.u), y: dot(l, b.v) };
    });
}
/** (u,v)-Meter-Punkt → Geo. */
function from2d(p, b) {
    return fromLocalMeters({ x: p.x * b.u.x + p.y * b.v.x, y: p.x * b.u.y + p.y * b.v.y }, b.origin);
}
/**
 * Bereinigt aufeinanderfolgende (fast) deckungsgleiche Polygon-Stützpunkte
 * (Null-Längen-Kanten / Duplikat-Vertices aus Digitalisierung, z. B. Google Solar).
 * Solche 0-m-Kanten machen das Polygon degeneriert/nicht-simpel → Belegung liefert 0
 * Module. Schwelle 0,3 m (kleiner als jede echte Dachkante, größer als Digitalisier-Rauschen).
 */
function dedupeVertices(vertices) {
    if (vertices.length < 2)
        return vertices;
    const origin = vertices[0];
    const out = [];
    let prev = null;
    for (const p of vertices) {
        const l = toLocalMeters(p, origin);
        if (prev && Math.hypot(l.x - prev.x, l.y - prev.y) < 0.3)
            continue;
        out.push(p);
        prev = l;
    }
    // Schließungs-Duplikat (letzter ~ erster Punkt) entfernen.
    if (out.length >= 2) {
        const f = toLocalMeters(out[0], origin);
        const last = toLocalMeters(out[out.length - 1], origin);
        if (Math.hypot(last.x - f.x, last.y - f.y) < 0.3)
            out.pop();
    }
    return out;
}
/** Belegt das Dach deterministisch mit Modulen. */
export function packModules(plane, obstacles, exclusions, opts) {
    // Degenerierte Stützpunkte (0-m-Kanten) vor der Rasterung entfernen — sonst 0 Module.
    const cleanVerts = dedupeVertices(plane.vertices);
    const cleanPlane = cleanVerts.length >= 3 ? { ...plane, vertices: cleanVerts } : plane;
    const basis = buildBasis(cleanPlane);
    const roof2d = to2d(cleanPlane.vertices, basis);
    const roofAreaM2 = polygonAreaPlanar(roof2d);
    const keepouts = [
        ...obstacles.map((o) => ({ poly: to2d(o.vertices, basis), clearanceM: obstacleClearanceM(o, opts.setbacks) })),
        ...exclusions.map((z) => ({ poly: to2d(z.vertices, basis), clearanceM: exclusionClearanceM(z) })),
    ];
    const setbackM = roofEdgeSetbackM(opts.setbacks);
    // Modul-Footprint im (u,v)-System
    const alongU = opts.orientation === "portrait" ? opts.moduleSpec.widthM : opts.moduleSpec.heightM;
    const alongV = opts.orientation === "portrait" ? opts.moduleSpec.heightM : opts.moduleSpec.widthM;
    const du = alongU + Math.max(0, opts.moduleGapM);
    const dv = alongV + Math.max(0, opts.rowGapM);
    // Bounding-Box des Dachs in (u,v)
    const xs = roof2d.map((p) => p.x);
    const ys = roof2d.map((p) => p.y);
    const uMin = Math.min(...xs);
    const uMax = Math.max(...xs);
    const vMin = Math.min(...ys);
    const vMax = Math.max(...ys);
    // Index-basiert (kein FP-Drift durch Akkumulation)
    const nRows = Math.max(0, Math.floor((vMax - vMin - alongV) / dv + EPS) + 1);
    const nCols = Math.max(0, Math.floor((uMax - uMin - alongU) / du + EPS) + 1);
    // P0-Schutz: ein überskaliertes/degeneriertes Polygon (RoofSurface.polygon ist freies
    // Json) darf KEINE unbeschränkte Rasterallokation auslösen — sonst Heap-OOM = Prozess-DoS
    // über alle Tenants. Reale Dächer haben < ~1000 Module; 50k ist eine sichere Obergrenze.
    // Überschreitung → leere Belegung (computeModuleLayout liefert needs_user_review).
    const MAX_GRID_CELLS = 50_000;
    if (!Number.isFinite(nRows) || !Number.isFinite(nCols) || nRows * nCols > MAX_GRID_CELLS) {
        return { modules: [], roofAreaM2 };
    }
    const modules = [];
    for (let r = 0; r < nRows; r++) {
        const v0 = vMin + r * dv;
        for (let c = 0; c < nCols; c++) {
            const u0 = uMin + c * du;
            const corners2d = [
                { x: u0, y: v0 },
                { x: u0 + alongU, y: v0 },
                { x: u0 + alongU, y: v0 + alongV },
                { x: u0, y: v0 + alongV },
            ];
            if (!moduleIsPlaceable(corners2d, roof2d, keepouts, setbackM))
                continue;
            modules.push({
                corners: corners2d.map((p) => from2d(p, basis)),
                orientation: opts.orientation,
                row: r,
                col: c,
            });
        }
    }
    return { modules, roofAreaM2 };
}
//# sourceMappingURL=raster.js.map